import { Fragment, useState, useEffect } from "react";
import { Dialog, Transition, Listbox } from "@headlessui/react";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronDoubleRightIcon,
  ShareIcon,
} from "@heroicons/react/outline";
import { Link } from "react-router-dom";

import * as api from "./apiService";
import Pagination from "react-js-pagination";
import Loader from "../../../components/loader/Loader";
import { toast } from "react-toastify";
import AnimatedPage from "../../../components/animatedPage/AnimatedPage";

import ExportJobModal from "../../job/ExportJobModal";

import { selectUser } from "../../../routes/userProfile/userSlice";
import { useAppSelector } from "../../../app/hooks";

// MUST match ReportDateRangeResolver._resolve_quick_range on the backend
function getQuickDateRange(dateSelectedId) {
  const today = new Date();

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  switch (dateSelectedId) {
    case "yesterday": {
      const d = addDays(today, -1);
      return {
        startDate: formatDate(d),
        endDate: formatDate(d),
      };
    }

    case "last7Days": {
      return {
        startDate: formatDate(addDays(today, -6)),
        endDate: formatDate(today),
      };
    }

    case "lastWeek": {
      // Backend: previous Monday-Sunday
      // JS getDay(): Sunday=0, Monday=1
      const jsDay = today.getDay();
      const daysSinceMonday = jsDay === 0 ? 6 : jsDay - 1;

      const startOfThisWeek = addDays(today, -daysSinceMonday);
      const end = addDays(startOfThisWeek, -1);
      const start = addDays(end, -6);

      return {
        startDate: formatDate(start),
        endDate: formatDate(end),
      };
    }

    case "MTD": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        startDate: formatDate(start),
        endDate: formatDate(today),
      };
    }

    case "lastMonth": {
      const firstOfThisMonth = new Date(
        today.getFullYear(),
        today.getMonth(),
        1,
      );
      const end = addDays(firstOfThisMonth, -1);
      const start = new Date(end.getFullYear(), end.getMonth(), 1);

      return {
        startDate: formatDate(start),
        endDate: formatDate(end),
      };
    }

    case "lastQuarter": {
      const currentQuarter = Math.floor(today.getMonth() / 3) + 1;
      let year = today.getFullYear();
      let quarter = currentQuarter - 1;

      if (currentQuarter === 1) {
        year -= 1;
        quarter = 4;
      }

      const quarterStartMonth = (quarter - 1) * 3;
      const start = new Date(year, quarterStartMonth, 1);
      const end = new Date(year, quarterStartMonth + 3, 0);

      return {
        startDate: formatDate(start),
        endDate: formatDate(end),
      };
    }

    case "YTD": {
      const start = new Date(today.getFullYear(), 0, 1);
      return {
        startDate: formatDate(start),
        endDate: formatDate(today),
      };
    }

    case "lastYear": {
      const start = new Date(today.getFullYear() - 1, 0, 1);
      const end = new Date(today.getFullYear() - 1, 11, 31);

      return {
        startDate: formatDate(start),
        endDate: formatDate(end),
      };
    }

    default:
      return {
        startDate: "",
        endDate: "",
      };
  }
}

const MagnifyingGlassIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="h-5 w-5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
      />
    </svg>
  );
};

const ChevronUpDownIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="h-5 w-5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
      />
    </svg>
  );
};

const XMarkIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="h-6 w-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
};

const dateOptions = [
  { id: "yesterday", name: "Yesterday" },
  { id: "last7Days", name: "Last 7 Days" },
  { id: "lastWeek", name: "Last Week" },
  { id: "MTD", name: "Month to Date" },
  { id: "lastMonth", name: "Last Month" },
  { id: "lastQuarter", name: "Last Quarter" },
  { id: "YTD", name: "Year to Date" },
  { id: "lastYear", name: "Last Year" },
];

const DATE_FILTER_MODES = {
  QUICK: "quick",
  CUSTOM: "custom",
};

function formatDateLabel(dateString) {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function SectionCard({ className = "", children }) {
  return (
    <div
      className={classNames(
        "rounded-2xl border border-slate-200 bg-white shadow-sm",
        className,
      )}
    >
      {children}
    </div>
  );
}

function StatCard({ label, value, helper, accent = "red" }) {
  const accentClass =
    accent === "sky"
      ? "from-sky-500/10 to-cyan-500/10 border-sky-200"
      : "from-red-500/10 to-rose-500/10 border-red-200";

  return (
    <SectionCard
      className={classNames(
        "relative overflow-hidden",
        "bg-gradient-to-br",
        accentClass,
      )}
    >
      <div className="p-5 sm:p-6">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          {label}
        </div>
        <div className="mt-3 text-3xl font-semibold tracking-tight text-slate-800 sm:text-4xl">
          {value}
        </div>
        {helper ? (
          <div className="mt-2 text-sm text-slate-500">{helper}</div>
        ) : null}
      </div>
    </SectionCard>
  );
}

function ToolbarLabel({ children }) {
  return (
    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
      {children}
    </label>
  );
}

function FilterShell({ children, active = false }) {
  return (
    <div
      className={classNames(
        "rounded-xl border bg-white shadow-sm transition",
        active
          ? "border-red-300 ring-2 ring-red-100"
          : "border-slate-200 hover:border-slate-300",
      )}
    >
      {children}
    </div>
  );
}

function SidebarServiceGroup({
  title,
  expanded,
  onToggle,
  items,
  selectedId,
  onSelect,
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/70 p-2 shadow-sm backdrop-blur-sm">
      <button
        type="button"
        onClick={onToggle}
        className={classNames(
          "flex w-full items-center justify-between rounded-xl px-3 py-3 text-left transition",
          expanded
            ? "bg-red-50 text-red-700"
            : "text-slate-700 hover:bg-slate-50 hover:text-slate-900",
        )}
      >
        <span className="text-sm font-semibold tracking-wide">{title}</span>
        {expanded ? (
          <ChevronUpIcon className="h-4 w-4" />
        ) : (
          <ChevronDownIcon className="h-4 w-4" />
        )}
      </button>

      {expanded ? (
        <div className="mt-2 space-y-1 border-t border-slate-100 pt-2">
          {items.map((service) => {
            const isActive = service.id === selectedId;
            return (
              <button
                key={service.id}
                type="button"
                onClick={() => onSelect(service)}
                className={classNames(
                  "flex w-full items-center rounded-xl px-3 py-2.5 text-left text-sm transition",
                  isActive
                    ? "bg-red-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-50 hover:text-red-700",
                )}
              >
                <span className="truncate">{service.name}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function ActivityMobileCard({ service, currentUser }) {
  return (
    <li className="px-4 py-4 sm:px-5">
      <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Completed
            </div>
            <div className="mt-1 text-sm font-medium text-slate-700">
              {service.invoice_date || service.timestamp}
            </div>
          </div>
          <div className="text-right">
            {!currentUser.isExternalProjectManager ? (
              <Link
                className="text-sm font-semibold text-sky-600 hover:text-sky-700"
                to={`/completed/review/${service.job_id}`}
              >
                {service.purchase_order}
              </Link>
            ) : (
              <span className="text-sm font-semibold text-slate-700">
                {service.purchase_order}
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 text-base font-semibold text-slate-900">
          {service.service_name || service.retainer_service_name}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-xl bg-white px-3 py-2 border border-slate-200">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">
              Tail
            </div>
            <div className="mt-1 font-medium text-slate-700">
              {service.tail_number}
            </div>
          </div>
          <div className="rounded-xl bg-white px-3 py-2 border border-slate-200">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">
              FBO
            </div>
            <div className="mt-1 font-medium text-slate-700 truncate">
              {service.fbo_name}
            </div>
          </div>
          <div className="col-span-2 rounded-xl bg-white px-3 py-2 border border-slate-200">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">
              Airport
            </div>
            <div className="mt-1 font-medium text-slate-700 break-words">
              {service.airport_name}
            </div>
          </div>
          {!currentUser.isCustomer && !currentUser.isExternalProjectManager ? (
            <div className="col-span-2 rounded-xl bg-white px-3 py-2 border border-slate-200">
              <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">
                Customer
              </div>
              <div className="mt-1 font-medium text-slate-700 break-words">
                {service.customer_name}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function EmptyState({ title, description }) {
  return (
    <SectionCard className="px-6 py-16 text-center">
      <div className="mx-auto max-w-md">
        <div className="text-xl font-semibold text-slate-900">{title}</div>
        <div className="mt-2 text-sm text-slate-500">{description}</div>
      </div>
    </SectionCard>
  );
}

export default function CustomerServiceReport() {
  const currentUser = useAppSelector(selectUser);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showRetainers, setShowRetainers] = useState(false);
  const [showSpendingInfo, setShowSpendingInfo] = useState(false);
  const [isStandardServicesSelected, setIsStandardServicesSelected] =
    useState(true);
  const [isRetainerServicesSelected, setIsRetainerServicesSelected] =
    useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState({});
  const [selectedRetainerServiceType, setSelectedRetainerServiceType] =
    useState({});
  const [selectedService, setSelectedService] = useState({
    id: null,
    name: "All Services",
  });
  const [selectedRetainerService, setSelectedRetainerService] = useState();

  const [dateSelected, setDateSelected] = useState(dateOptions[3]);
  const [dateFilterMode, setDateFilterMode] = useState(DATE_FILTER_MODES.QUICK);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const [searchText, setSearchText] = useState("");
  const [interiorServices, setInteriorServices] = useState([]);
  const [exteriorServices, setExteriorServices] = useState([]);
  const [otherServices, setOtherServices] = useState([]);
  const [retainerServices, setRetainerServices] = useState([]);
  const [retainerInteriorServices, setRetainerInteriorServices] = useState([]);
  const [retainerExteriorServices, setRetainerExteriorServices] = useState([]);
  const [retainerOtherServices, setRetainerOtherServices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentRetainerPage, setCurrentRetainerPage] = useState(1);
  const [serviceActivities, setServiceActivities] = useState([]);
  const [totalServiceActivities, setTotalServiceActivities] = useState(0);
  const [retainerServiceActivities, setRetainerServiceActivities] = useState(
    [],
  );
  const [totalRetainerServiceActivities, setTotalRetainerServiceActivities] =
    useState(0);
  const [numberOfServices, setNumberOfServices] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);
  const [totalSpentNotInvoiced, setTotalSpentNotInvoiced] = useState(0);
  const [numberOfTails, setNumberOfTails] = useState(0);
  const [numberOfLocations, setNumberOfLocations] = useState(0);
  const [totalLaborTime, setTotalLaborTime] = useState(0);
  const [airportSelected, setAirportSelected] = useState(null);
  const [fboSelected, setFboSelected] = useState(null);
  const [customerSelected, setCustomerSelected] = useState(null);
  const [airports, setAirports] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [fbos, setFbos] = useState([]);
  const [allFbos, setAllFbos] = useState([]);
  const [airportSearchTerm, setAirportSearchTerm] = useState("");
  const [fboSearchTerm, setFboSearchTerm] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [sortByPriceDesc, setSortByPriceDesc] = useState(false);
  const [sortByPriceAsc, setSortByPriceAsc] = useState(false);
  const [sortByTimestampAsc, setSortByTimestampAsc] = useState(false);
  const [sortByTimestampDesc, setSortByTimestampDesc] = useState(true);

  const [isExportJobModalOpen, setExportJobModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const isCustomDateRangeComplete = Boolean(customStartDate && customEndDate);
  const isCustomDateRangeValid =
    isCustomDateRangeComplete && customStartDate <= customEndDate;

  const dateRangeLabel =
    dateFilterMode === DATE_FILTER_MODES.QUICK
      ? dateSelected.name
      : isCustomDateRangeComplete
        ? `${formatDateLabel(customStartDate)} - ${formatDateLabel(customEndDate)}`
        : "Custom Range";

  useEffect(() => {
    const range = getQuickDateRange(dateOptions[3].id);
    setCustomStartDate(range.startDate);
    setCustomEndDate(range.endDate);
  }, []);

  useEffect(() => {
    getServices();
  }, [customerSelected]);

  useEffect(() => {
    getRetainerServices();
  }, [customerSelected]);

  useEffect(() => {
    getAirports();
  }, [airportSearchTerm]);

  useEffect(() => {
    getFbos();
  }, [fboSearchTerm]);

  useEffect(() => {
    getCustomers();
  }, [customerSearchTerm]);

  useEffect(() => {
    const timeoutID = setTimeout(() => {
      if (!isStandardServicesSelected) return;

      if (
        dateFilterMode === DATE_FILTER_MODES.CUSTOM &&
        !isCustomDateRangeValid
      ) {
        return;
      }

      generateServiceReport(selectedService);
    }, 300);

    return () => clearTimeout(timeoutID);
  }, [
    dateFilterMode,
    dateSelected,
    customStartDate,
    customEndDate,
    searchText,
    airportSelected,
    fboSelected,
    customerSelected,
    isStandardServicesSelected,
  ]);

  useEffect(() => {
    const timeoutID = setTimeout(() => {
      if (!isRetainerServicesSelected) return;

      if (
        dateFilterMode === DATE_FILTER_MODES.CUSTOM &&
        !isCustomDateRangeValid
      ) {
        return;
      }

      generateRetainerServiceReport(selectedRetainerService);
    }, 300);

    return () => clearTimeout(timeoutID);
  }, [
    dateFilterMode,
    dateSelected,
    customStartDate,
    customEndDate,
    searchText,
    airportSelected,
    fboSelected,
    customerSelected,
    isRetainerServicesSelected,
  ]);

  useEffect(() => {
    searchServiceActivities(null, selectedService);
  }, [currentPage, sortByTimestampAsc, sortByTimestampDesc]);

  useEffect(() => {
    searchRetainerServiceActivities(null, selectedRetainerService);
  }, [currentRetainerPage]);

  const handleToggleExportJobModal = () => {
    setExportJobModalOpen(!isExportJobModalOpen);
  };

  const handleQuickDateOptionChange = (option) => {
    const range = getQuickDateRange(option.id);

    setDateSelected(option);
    setDateFilterMode(DATE_FILTER_MODES.QUICK);
    setCustomStartDate(range.startDate);
    setCustomEndDate(range.endDate);
  };

  const handleCustomeStartDateOptionChange = (event) => {
    setCustomStartDate(event.target.value);
    setDateFilterMode(DATE_FILTER_MODES.CUSTOM);
  };

  const handleCustomeEndDateOptionChange = (event) => {
    setCustomEndDate(event.target.value);
    setDateFilterMode(DATE_FILTER_MODES.CUSTOM);
  };

  const getAirports = async () => {
    const request = { name: airportSearchTerm };
    try {
      const { data } = await api.getAirports(request);
      data.results.unshift({ id: null, name: "All Airports" });
      setAirports(data.results);
    } catch (err) {
      toast.error("Unable to get airports");
    }
  };

  const getFbos = async () => {
    const request = { name: fboSearchTerm };
    try {
      const { data } = await api.searchFbos(request);
      data.results.unshift({ id: null, name: "All FBOs" });
      setAllFbos(data.results);
      setFbos(data.results);
    } catch (err) {
      toast.error("Unable to get fbos");
    }
  };

  const getCustomers = async () => {
    const { data } = await api.getCustomers({ name: customerSearchTerm });
    data.results.unshift({ id: null, name: "All Customers" });
    setCustomers(data.results);
  };

  const getServices = async () => {
    const request = { customer_id: customerSelected?.id };
    try {
      const { data } = await api.getServices(request);
      setInteriorServices(
        data.results.filter((service) => service.category === "I"),
      );
      setExteriorServices(
        data.results.filter((service) => service.category === "E"),
      );
      setOtherServices(
        data.results.filter((service) => service.category === "O"),
      );
    } catch (err) {
      toast.error("Unable to get services");
    }
  };

  const getRetainerServices = async () => {
    const request = { customer_id: customerSelected?.id };
    try {
      const { data } = await api.getRetainerServices(request);
      setRetainerInteriorServices(
        data.results.filter((service) => service.category === "I"),
      );
      setRetainerExteriorServices(
        data.results.filter((service) => service.category === "E"),
      );
      setRetainerOtherServices(
        data.results.filter((service) => service.category === "O"),
      );
      setRetainerServices(data.results);
    } catch (err) {
      toast.error("Unable to get retainers");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      generateServiceReport();
    }
  };

  const handleServiceTypeChange = (serviceType) => {
    if (selectedServiceType.type === serviceType.type) {
      setSelectedServiceType({});
    } else {
      setSelectedServiceType(serviceType);
    }
    setSelectedRetainerServiceType({});
  };

  const handleRetainerServiceTypeChange = (serviceType) => {
    if (selectedRetainerServiceType.type === serviceType.type) {
      setSelectedRetainerServiceType({});
    } else {
      setSelectedRetainerServiceType(serviceType);
    }
    setSelectedServiceType({});
  };

  const handleServiceChange = (service) => {
    setCurrentPage(1);
    setSelectedRetainerService(null);
    setIsRetainerServicesSelected(false);
    setIsStandardServicesSelected(true);
    generateServiceReport(service);
  };

  const handleRetainerServiceChange = (service) => {
    setCurrentRetainerPage(1);
    setSelectedService(null);
    setIsStandardServicesSelected(false);
    setIsRetainerServicesSelected(true);
    generateRetainerServiceReport(service);
  };

  const generateServiceReport = async (service) => {
    setLoading(true);
    const request = {
      service_id: service?.id,
      ...buildDateFilterPayload(),
      tail_number: searchText,
      airport_id: airportSelected?.id,
      fbo_id: fboSelected?.id,
      customer_id: customerSelected?.id,
    };

    if (service) setSelectedService(service);

    try {
      const { data } = await api.generateServiceReport(request);
      setNumberOfServices(data.number_of_services_completed);
      setTotalSpent(data.total_jobs_revenue);
      setTotalSpentNotInvoiced(data.total_jobs_revenue_not_invoiced);
      setNumberOfTails(data.number_of_unique_tail_numbers);
      setNumberOfLocations(data.number_of_unique_locations);
      setShowSpendingInfo(data.show_spending_info);
      setShowRetainers(data.show_retainers);
      setTotalLaborTime(data.total_labor_time_only_services);
    } catch (err) {
      toast.error("Unable to generate service report");
    }

    setLoading(false);
    searchServiceActivities(1, service);
  };

  const generateRetainerServiceReport = async (retainerService) => {
    setLoading(true);
    const request = {
      service_id: retainerService?.id,
      ...buildDateFilterPayload(),
      tail_number: searchText,
      airport_id: airportSelected?.id,
      fbo_id: fboSelected?.id,
      customer_id: customerSelected?.id,
    };

    if (retainerService) setSelectedRetainerService(retainerService);

    try {
      const { data } = await api.generateRetainerServiceReport(request);
      setNumberOfServices(data.number_of_services_completed);
      setNumberOfTails(data.number_of_unique_tail_numbers);
      setNumberOfLocations(data.number_of_unique_locations);
      setTotalLaborTime(data.total_labor_time_only_retainer_services);
    } catch (err) {
      toast.error("Unable to generate service report");
    }

    setLoading(false);
    searchRetainerServiceActivities(1, retainerService);
  };

  const searchServiceActivities = async (page, service) => {
    setActivitiesLoading(true);
    const request = {
      service_id: service?.id,
      ...buildDateFilterPayload(),
      tail_number: searchText,
      airport_id: airportSelected?.id,
      fbo_id: fboSelected?.id,
      customer_id: customerSelected?.id,
      sort_by_price_asc: sortByPriceAsc,
      sort_by_price_desc: sortByPriceDesc,
      sort_by_timestamp_asc: sortByTimestampAsc,
      sort_by_timestamp_desc: sortByTimestampDesc,
    };

    let cPage = currentPage;
    if (page) cPage = page;

    try {
      const { data } = await api.searchServiceActivities(request, cPage);
      setServiceActivities(data.results);
      setTotalServiceActivities(data.count);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Unable to search service activities");
    }

    setActivitiesLoading(false);
  };

  const searchRetainerServiceActivities = async (page, retainerService) => {
    setActivitiesLoading(true);
    const request = {
      service_id: retainerService?.id,
      ...buildDateFilterPayload(),
      tail_number: searchText,
      airport_id: airportSelected?.id,
      fbo_id: fboSelected?.id,
      customer_id: customerSelected?.id,
      sort_by_price_asc: sortByPriceAsc,
      sort_by_price_desc: sortByPriceDesc,
      sort_by_timestamp_asc: sortByTimestampAsc,
      sort_by_timestamp_desc: sortByTimestampDesc,
    };

    let cPage = currentRetainerPage;
    if (page) cPage = page;

    try {
      const { data } = await api.searchRetainerServiceActivities(
        request,
        cPage,
      );
      setRetainerServiceActivities(data.results);
      setTotalRetainerServiceActivities(data.count);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Unable to search retainer service activities");
    }

    setActivitiesLoading(false);
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRetainerPageChange = (page) => setCurrentRetainerPage(page);

  const handleSortByTimestamp = () => {
    if (sortByTimestampDesc) {
      setSortByTimestampDesc(false);
      setSortByTimestampAsc(true);
    } else {
      setSortByTimestampDesc(true);
      setSortByTimestampAsc(false);
    }
    setSortByPriceAsc(false);
    setSortByPriceDesc(false);
  };

  const handleAirportSelectedChange = async (airport) => {
    setAirportSelected(airport);
    const request = { airport_id: airport.id };
    try {
      const { data } = await api.searchFbos(request);
      if (data.results.length > 0) {
        setFbos(data.results);
      } else {
        setFbos(allFbos);
      }
      data.results.unshift({ id: null, name: "All FBOs" });
    } catch (err) {
      toast.error("Unable to get Fbos");
    }
  };

  const handleCustomerSelectedChange = (customer) => {
    setCustomerSelected(customer);

    setCurrentPage(1);
    setCurrentRetainerPage(1);

    setSelectedService({ id: null, name: "All Services" });
    setSelectedRetainerService(null);

    setIsStandardServicesSelected(true);
    setIsRetainerServicesSelected(false);

    setServiceActivities([]);
    setTotalServiceActivities(0);
    setRetainerServiceActivities([]);
    setTotalRetainerServiceActivities(0);
  };

  const handleSelectStandServices = () => {
    setCurrentPage(1);
    setIsStandardServicesSelected(true);
    setIsRetainerServicesSelected(false);
    generateServiceReport();
  };

  const handleSelectRetainerServices = () => {
    setCurrentPage(1);
    setIsStandardServicesSelected(false);
    setIsRetainerServicesSelected(true);
    generateRetainerServiceReport();
  };

  const handleClearCustomerSearchFilter = () => {
    setCustomerSearchTerm("");
    setCustomerSelected(null);
  };

  const handleClearAirportSearchFilter = () => {
    setAirportSearchTerm("");
    setAirportSelected(null);
  };

  const handleClearFboSearchFilter = () => {
    setFboSearchTerm("");
    setFboSelected(null);
  };

  const buildDateFilterPayload = () => ({
    date_filter_mode: dateFilterMode,
    date_selected:
      dateFilterMode === DATE_FILTER_MODES.QUICK ? dateSelected.id : null,
    start_date:
      dateFilterMode === DATE_FILTER_MODES.CUSTOM
        ? customStartDate || null
        : null,
    end_date:
      dateFilterMode === DATE_FILTER_MODES.CUSTOM
        ? customEndDate || null
        : null,
  });

  const handleDateFilterModeChange = (mode) => {
    setDateFilterMode(mode);
  };

  const handleCustomStartDateChange = (event) => {
    setCustomStartDate(event.target.value);
  };

  const handleCustomEndDateChange = (event) => {
    setCustomEndDate(event.target.value);
  };

  const handleClearCustomDates = () => {
    setCustomStartDate("");
    setCustomEndDate("");
  };

  const getCurrentReportScope = () => {
    if (isRetainerServicesSelected) return "retainer";
    return "standard";
  };

  const getCurrentExportServiceId = () => {
    if (isRetainerServicesSelected) {
      return selectedRetainerService?.id || null;
    }

    return selectedService?.id || null;
  };

  const buildServiceReportExportPayload = () => ({
    job_type: "S",
    params: {
      report_scope: getCurrentReportScope(),
      ...buildDateFilterPayload(),
      tail_number: searchText || "",
      airport_id: airportSelected?.id || null,
      fbo_id: fboSelected?.id || null,
      customer_id: customerSelected?.id || null,
      service_id: getCurrentExportServiceId(),
    },
  });

  const handleExport = async () => {
    if (
      dateFilterMode === DATE_FILTER_MODES.CUSTOM &&
      !isCustomDateRangeValid
    ) {
      toast.error("Please select a valid start and end date before exporting.");
      return;
    }

    setExportLoading(true);

    const request = buildServiceReportExportPayload();

    try {
      await api.exportJobsAsync(request);

      toast.success(
        "Export request created. You’ll receive an email when the file is ready.",
      );

      setExportJobModalOpen(false);
    } catch (error) {
      toast.error("Unable to start service report export");
    } finally {
      setExportLoading(false);
    }
  };

  const pageTitle =
    selectedService?.name || selectedRetainerService?.name || "All Services";
  const showCombinedView =
    selectedService?.name === "All Services" &&
    showRetainers &&
    selectedRetainerService == null;

  const renderServiceDrawer = () => (
    <div className="space-y-6">
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
          Overview
        </div>
        <button
          type="button"
          onClick={() =>
            handleServiceChange({
              id: null,
              name: "All Services",
            })
          }
          className={classNames(
            "flex w-full items-center rounded-2xl px-4 py-3 text-left text-sm font-semibold transition",
            selectedService?.id === null && !selectedRetainerService
              ? "bg-red-600 text-white shadow-sm"
              : "border border-slate-200 bg-white text-slate-700 hover:border-red-200 hover:text-red-700",
          )}
        >
          <span className="truncate">All Services</span>
        </button>
      </div>

      <div>
        <div className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
          Standard Services
        </div>
        <div className="space-y-3">
          <SidebarServiceGroup
            title="Exterior"
            expanded={selectedServiceType.type === "E"}
            onToggle={() =>
              handleServiceTypeChange({ type: "E", name: "Exterior" })
            }
            items={exteriorServices}
            selectedId={selectedService?.id}
            onSelect={handleServiceChange}
          />
          <SidebarServiceGroup
            title="Interior"
            expanded={selectedServiceType.type === "I"}
            onToggle={() =>
              handleServiceTypeChange({ type: "I", name: "Interior" })
            }
            items={interiorServices}
            selectedId={selectedService?.id}
            onSelect={handleServiceChange}
          />
          <SidebarServiceGroup
            title="Other"
            expanded={selectedServiceType.type === "O"}
            onToggle={() =>
              handleServiceTypeChange({ type: "O", name: "Other" })
            }
            items={otherServices}
            selectedId={selectedService?.id}
            onSelect={handleServiceChange}
          />
        </div>
      </div>

      {showRetainers ? (
        <div className="border-t border-slate-200 pt-6">
          <div className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
            Retainer Services
          </div>
          <div className="space-y-3">
            <SidebarServiceGroup
              title="Exterior"
              expanded={selectedRetainerServiceType.type === "E"}
              onToggle={() =>
                handleRetainerServiceTypeChange({ type: "E", name: "Exterior" })
              }
              items={retainerExteriorServices}
              selectedId={selectedRetainerService?.id}
              onSelect={handleRetainerServiceChange}
            />
            <SidebarServiceGroup
              title="Interior"
              expanded={selectedRetainerServiceType.type === "I"}
              onToggle={() =>
                handleRetainerServiceTypeChange({ type: "I", name: "Interior" })
              }
              items={retainerInteriorServices}
              selectedId={selectedRetainerService?.id}
              onSelect={handleRetainerServiceChange}
            />
            <SidebarServiceGroup
              title="Other"
              expanded={selectedRetainerServiceType.type === "O"}
              onToggle={() =>
                handleRetainerServiceTypeChange({ type: "O", name: "Other" })
              }
              items={retainerOtherServices}
              selectedId={selectedRetainerService?.id}
              onSelect={handleRetainerServiceChange}
            />
          </div>
        </div>
      ) : null}
    </div>
  );

  const renderActivityTable = (
    rows,
    totalCount,
    title,
    description,
    page,
    onPageChange,
  ) => (
    <SectionCard className="overflow-hidden">
      <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
            <p className="mt-1 text-sm text-slate-500">{description}</p>
          </div>
          <button
            type="button"
            onClick={handleSortByTimestamp}
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
          >
            Sort by date {sortByTimestampDesc ? "↓" : "↑"}
          </button>
        </div>
      </div>

      <div className="hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Invoiced Date
                </th>
                <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Purchase Order
                </th>
                {!currentUser.isCustomer &&
                !currentUser.isExternalProjectManager ? (
                  <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Customer
                  </th>
                ) : null}
                <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Tail
                </th>
                <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Airport
                </th>
                <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  FBO
                </th>
                <th className="px-5 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Service
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {rows.map((service) => (
                <tr
                  key={service.id}
                  className="transition hover:bg-slate-50/80"
                >
                  <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-600">
                    {service.invoice_date}
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 text-sm">
                    {!currentUser.isExternalProjectManager ? (
                      <Link
                        className="font-semibold text-sky-600 hover:text-sky-700"
                        to={`/completed/review/${service.job_id}`}
                      >
                        {service.purchase_order}
                      </Link>
                    ) : (
                      <span className="font-medium text-slate-700">
                        {service.purchase_order}
                      </span>
                    )}
                  </td>
                  {!currentUser.isCustomer &&
                  !currentUser.isExternalProjectManager ? (
                    <td className="px-5 py-4 text-sm text-slate-600">
                      <div className="max-w-[220px] truncate">
                        {service.customer_name}
                      </div>
                    </td>
                  ) : null}
                  <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-600">
                    {service.tail_number}
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600">
                    <div className="max-w-[220px] truncate">
                      {service.airport_name}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600">
                    <div className="max-w-[180px] truncate">
                      {service.fbo_name}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600">
                    <div className="max-w-[380px] truncate">
                      {service.service_name || service.retainer_service_name}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="md:hidden">
        <ul className="divide-y divide-slate-200">
          {rows.map((service) => (
            <ActivityMobileCard
              key={service.id}
              service={service}
              currentUser={currentUser}
            />
          ))}
        </ul>
      </div>

      {totalCount > 200 ? (
        <div className="border-t border-slate-200 px-4 py-5 sm:px-6">
          <div className="flex justify-end overflow-x-auto">
            <Pagination
              innerClass="pagination pagination-custom"
              activePage={page}
              hideDisabled
              itemClass="page-item page-item-custom"
              linkClass="page-link page-link-custom"
              itemsCountPerPage={200}
              totalItemsCount={totalCount}
              pageRangeDisplayed={3}
              onChange={onPageChange}
            />
          </div>
        </div>
      ) : null}
    </SectionCard>
  );

  return (
    <div>
      <div className="mx-auto -mt-6 flex w-full max-w-[2400px] gap-6 px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10">
        <aside className="sticky top-4 hidden h-[calc(100vh-2rem)] w-[320px] shrink-0 xl:block">
          <div className="flex h-full flex-col rounded-[28px] border border-slate-200 bg-slate-100/90 p-5 shadow-sm backdrop-blur-sm">
            <div className="mb-6 border-b border-slate-200 pb-5">
              <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                Dashboard
              </div>
              <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">
                Service Report
              </div>
              <div className="mt-1 text-sm text-slate-500">
                Browse service categories and drill into activity.
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              {renderServiceDrawer()}
            </div>
          </div>
        </aside>

        <Transition.Root show={open} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-40 xl:hidden"
            onClose={setOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="relative ml-auto flex h-full w-full max-w-sm flex-col overflow-y-auto bg-white p-5 shadow-2xl">
                  <div className="mb-5 flex items-start justify-between border-b border-slate-200 pb-4">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                        Services
                      </div>
                      <h2 className="mt-2 text-2xl font-semibold text-slate-900">
                        Filters & Services
                      </h2>
                    </div>
                    <button
                      type="button"
                      className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                      onClick={() => setOpen(false)}
                    >
                      <XMarkIcon />
                    </button>
                  </div>
                  {renderServiceDrawer()}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        <main className="min-w-0 flex-1 pb-8 xl:max-w-none">
          <div className="xl:hidden mb-4">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
              onClick={() => setOpen(true)}
            >
              <span>Services</span>
              <ChevronDoubleRightIcon className="h-4 w-4" />
            </button>
          </div>

          <SectionCard className="overflow-visible">
            <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-800 px-5 py-6 sm:px-6 lg:px-8">
              <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-300">
                    Livetakeoff Analytics
                  </div>
                  <h1 className="mt-2 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                    {pageTitle}
                  </h1>
                  <p className="mt-2 max-w-2xl text-sm text-slate-300 sm:text-base">
                    Review service volume, locations, labor, and activity
                    details across your selected filters.
                  </p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center xl:justify-end">
                  <div className="inline-flex items-center rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200 backdrop-blur-sm">
                    <span className="font-semibold">Date range:</span>
                    <span className="ml-2 text-white">{dateRangeLabel}</span>
                  </div>

                  <button
                    type="button"
                    disabled={exportLoading}
                    onClick={handleToggleExportJobModal}
                    className="inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white px-4 py-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <ShareIcon className="mr-2 h-4 w-4" />
                    {exportLoading ? "Starting..." : "Export"}
                  </button>
                </div>
              </div>
            </div>

            <div className="relative z-20 bg-slate-50/80 px-4 py-5 sm:px-6 lg:px-8">
              <div className="space-y-5">
                <div className="rounded-[24px] border border-slate-200/80 bg-white/70 p-4 shadow-sm backdrop-blur-sm sm:p-5">
                  <div className="space-y-5">
                    <div className="flex justify-center">
                      <div className="w-full">
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(220px,280px)_minmax(160px,1fr)_minmax(160px,1fr)] lg:items-end">
                          <div>
                            <ToolbarLabel>Report Period</ToolbarLabel>
                            <FilterShell active={false}>
                              <Listbox
                                value={dateSelected}
                                onChange={handleQuickDateOptionChange}
                              >
                                {({ open }) => (
                                  <div className="relative z-[70]">
                                    <Listbox.Button className="relative w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 pr-9 text-left text-sm font-semibold text-slate-800 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600">
                                      <span className="block truncate">
                                        {dateSelected.name}
                                      </span>
                                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500">
                                        <ChevronDownIcon className="h-4 w-4" />
                                      </span>
                                    </Listbox.Button>

                                    <Transition
                                      show={open}
                                      as={Fragment}
                                      leave="transition ease-in duration-100"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                    >
                                      <Listbox.Options className="absolute left-0 z-[140] mt-1 max-h-80 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-xl focus:outline-none">
                                        {dateOptions.map((option) => (
                                          <Listbox.Option
                                            key={option.id}
                                            value={option}
                                            className={({ active }) =>
                                              classNames(
                                                "relative cursor-pointer select-none py-2.5 pl-9 pr-3 text-sm",
                                                active
                                                  ? "bg-slate-50 text-slate-900"
                                                  : "text-slate-700",
                                              )
                                            }
                                          >
                                            {({ selected }) => (
                                              <>
                                                {selected ? (
                                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                                    <CheckIcon className="h-5 w-5" />
                                                  </span>
                                                ) : null}
                                                <span
                                                  className={classNames(
                                                    selected
                                                      ? "font-semibold"
                                                      : "font-medium",
                                                    "block truncate",
                                                  )}
                                                >
                                                  {option.name}
                                                </span>
                                              </>
                                            )}
                                          </Listbox.Option>
                                        ))}

                                        <div className="my-1 border-t border-slate-100" />

                                        <button
                                          type="button"
                                          onClick={() =>
                                            setDateFilterMode(
                                              DATE_FILTER_MODES.CUSTOM,
                                            )
                                          }
                                          className={classNames(
                                            "flex w-full items-center px-3 py-2.5 text-left text-sm font-medium hover:bg-slate-50",
                                            dateFilterMode ===
                                              DATE_FILTER_MODES.CUSTOM
                                              ? "text-blue-700"
                                              : "text-slate-700",
                                          )}
                                        >
                                          {dateFilterMode ===
                                          DATE_FILTER_MODES.CUSTOM ? (
                                            <CheckIcon className="mr-3 h-5 w-5 text-blue-600" />
                                          ) : (
                                            <span className="mr-3 h-5 w-5" />
                                          )}
                                          Custom dates
                                        </button>
                                      </Listbox.Options>
                                    </Transition>
                                  </div>
                                )}
                              </Listbox>
                            </FilterShell>
                          </div>

                          <div>
                            <ToolbarLabel>From</ToolbarLabel>
                            <input
                              type="date"
                              value={customStartDate}
                              onChange={(event) =>
                                handleCustomeStartDateOptionChange(event)
                              }
                              className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
                            />
                          </div>

                          <div>
                            <ToolbarLabel>To</ToolbarLabel>
                            <input
                              type="date"
                              value={customEndDate}
                              min={customStartDate || undefined}
                              onChange={(event) =>
                                handleCustomeEndDateOptionChange(event)
                              }
                              className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
                            />
                          </div>

                          {dateFilterMode === DATE_FILTER_MODES.CUSTOM &&
                          isCustomDateRangeComplete &&
                          !isCustomDateRangeValid ? (
                            <div className="lg:col-span-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                              End date must be the same as or later than start
                              date.
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-slate-200 pt-5">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
                        <div>
                          <ToolbarLabel>Tail Search</ToolbarLabel>
                          <FilterShell active={searchText.length > 0}>
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => generateServiceReport()}
                                className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400"
                              >
                                <MagnifyingGlassIcon />
                              </button>
                              <input
                                type="search"
                                name="search"
                                id="search"
                                value={searchText}
                                onChange={(event) =>
                                  setSearchText(event.target.value)
                                }
                                onKeyDown={handleKeyDown}
                                className="block w-full rounded-xl border-0 bg-white py-3.5 pl-11 pr-4 text-sm font-medium text-slate-800 placeholder:text-slate-400 focus:ring-0"
                                placeholder="Search by tail..."
                              />
                            </div>
                          </FilterShell>
                        </div>

                        <div>
                          <ToolbarLabel>Airport</ToolbarLabel>
                          <FilterShell
                            active={
                              airportSelected &&
                              airportSelected.name !== "All Airports"
                            }
                          >
                            <Listbox
                              value={airportSelected}
                              onChange={handleAirportSelectedChange}
                            >
                              {({ open }) => (
                                <div className="relative z-[60]">
                                  <Listbox.Button className="relative w-full rounded-xl bg-white px-4 py-3.5 pr-10 text-left text-sm font-medium text-slate-700 focus:outline-none">
                                    <span className="block truncate">
                                      {airportSelected
                                        ? airportSelected.name
                                        : "Select airport"}
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400">
                                      <ChevronUpDownIcon />
                                    </span>
                                  </Listbox.Button>
                                  <Transition
                                    show={open}
                                    as={Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                  >
                                    <Listbox.Options className="absolute z-[120] mt-2 max-h-96 w-full overflow-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl focus:outline-none">
                                      <div className="sticky top-0 z-20 rounded-xl bg-white p-1">
                                        <div className="relative">
                                          <input
                                            type="text"
                                            value={airportSearchTerm}
                                            onChange={(e) =>
                                              setAirportSearchTerm(
                                                e.target.value,
                                              )
                                            }
                                            className="block w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-3 pr-12 text-sm focus:border-sky-500 focus:outline-none focus:ring-0"
                                            placeholder="Search airports"
                                          />
                                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400">
                                            {airportSearchTerm ? (
                                              <button
                                                type="button"
                                                onClick={
                                                  handleClearAirportSearchFilter
                                                }
                                                className="mr-2 text-sky-500"
                                              >
                                                ✕
                                              </button>
                                            ) : null}
                                            <MagnifyingGlassIcon />
                                          </div>
                                        </div>
                                      </div>
                                      <div className="mt-2 space-y-1">
                                        {airports.map((airport) => (
                                          <Listbox.Option
                                            key={airport.id}
                                            className={({ active }) =>
                                              classNames(
                                                "relative cursor-pointer rounded-xl py-3 pl-3 pr-9 text-sm",
                                                active
                                                  ? "bg-red-600 text-white"
                                                  : "text-slate-700",
                                              )
                                            }
                                            value={airport}
                                          >
                                            {({ selected, active }) => (
                                              <>
                                                <span
                                                  className={classNames(
                                                    selected
                                                      ? "font-semibold"
                                                      : "font-medium",
                                                    "block truncate",
                                                  )}
                                                >
                                                  {airport.name}
                                                </span>
                                                {selected ? (
                                                  <span
                                                    className={classNames(
                                                      active
                                                        ? "text-white"
                                                        : "text-red-600",
                                                      "absolute inset-y-0 right-0 flex items-center pr-4",
                                                    )}
                                                  >
                                                    <CheckIcon className="h-5 w-5" />
                                                  </span>
                                                ) : null}
                                              </>
                                            )}
                                          </Listbox.Option>
                                        ))}
                                      </div>
                                    </Listbox.Options>
                                  </Transition>
                                </div>
                              )}
                            </Listbox>
                          </FilterShell>
                        </div>

                        <div>
                          <ToolbarLabel>FBO</ToolbarLabel>
                          <FilterShell
                            active={
                              fboSelected && fboSelected.name !== "All FBOs"
                            }
                          >
                            <Listbox
                              value={fboSelected}
                              onChange={setFboSelected}
                            >
                              {({ open }) => (
                                <div className="relative z-[50]">
                                  <Listbox.Button className="relative w-full rounded-xl bg-white px-4 py-3.5 pr-10 text-left text-sm font-medium text-slate-700 focus:outline-none">
                                    <span className="block truncate">
                                      {fboSelected
                                        ? fboSelected.name
                                        : "Select FBO"}
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400">
                                      <ChevronUpDownIcon />
                                    </span>
                                  </Listbox.Button>
                                  <Transition
                                    show={open}
                                    as={Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                  >
                                    <Listbox.Options className="absolute z-[120] mt-2 max-h-96 w-full overflow-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl focus:outline-none">
                                      <div className="sticky top-0 z-20 rounded-xl bg-white p-1">
                                        <div className="relative">
                                          <input
                                            type="text"
                                            value={fboSearchTerm}
                                            onChange={(e) =>
                                              setFboSearchTerm(e.target.value)
                                            }
                                            className="block w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-3 pr-12 text-sm focus:border-sky-500 focus:outline-none focus:ring-0"
                                            placeholder="Search FBOs"
                                          />
                                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400">
                                            {fboSearchTerm ? (
                                              <button
                                                type="button"
                                                onClick={
                                                  handleClearFboSearchFilter
                                                }
                                                className="mr-2 text-sky-500"
                                              >
                                                ✕
                                              </button>
                                            ) : null}
                                            <MagnifyingGlassIcon />
                                          </div>
                                        </div>
                                      </div>
                                      <div className="mt-2 space-y-1">
                                        {fbos.map((fbo) => (
                                          <Listbox.Option
                                            key={fbo.id}
                                            className={({ active }) =>
                                              classNames(
                                                "relative cursor-pointer rounded-xl py-3 pl-3 pr-9 text-sm",
                                                active
                                                  ? "bg-red-600 text-white"
                                                  : "text-slate-700",
                                              )
                                            }
                                            value={fbo}
                                          >
                                            {({ selected, active }) => (
                                              <>
                                                <span
                                                  className={classNames(
                                                    selected
                                                      ? "font-semibold"
                                                      : "font-medium",
                                                    "block truncate",
                                                  )}
                                                >
                                                  {fbo.name}
                                                </span>
                                                {selected ? (
                                                  <span
                                                    className={classNames(
                                                      active
                                                        ? "text-white"
                                                        : "text-red-600",
                                                      "absolute inset-y-0 right-0 flex items-center pr-4",
                                                    )}
                                                  >
                                                    <CheckIcon className="h-5 w-5" />
                                                  </span>
                                                ) : null}
                                              </>
                                            )}
                                          </Listbox.Option>
                                        ))}
                                      </div>
                                    </Listbox.Options>
                                  </Transition>
                                </div>
                              )}
                            </Listbox>
                          </FilterShell>
                        </div>

                        <div>
                          <ToolbarLabel>Customer</ToolbarLabel>
                          {!currentUser.isExternalProjectManager ? (
                            <FilterShell
                              active={
                                customerSelected &&
                                customerSelected.name !== "All Customers"
                              }
                            >
                              <Listbox
                                value={customerSelected}
                                onChange={handleCustomerSelectedChange}
                              >
                                {({ open }) => (
                                  <div className="relative z-[40]">
                                    <Listbox.Button className="relative w-full rounded-xl bg-white px-4 py-3.5 pr-10 text-left text-sm font-medium text-slate-700 focus:outline-none">
                                      <span className="block truncate">
                                        {customerSelected
                                          ? customerSelected.name
                                          : "Select customer"}
                                      </span>
                                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400">
                                        <ChevronUpDownIcon />
                                      </span>
                                    </Listbox.Button>
                                    <Transition
                                      show={open}
                                      as={Fragment}
                                      leave="transition ease-in duration-100"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                    >
                                      <Listbox.Options className="absolute z-[120] mt-2 max-h-96 w-full overflow-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl focus:outline-none">
                                        <div className="sticky top-0 z-20 rounded-xl bg-white p-1">
                                          <div className="relative">
                                            <input
                                              type="text"
                                              value={customerSearchTerm}
                                              onChange={(e) =>
                                                setCustomerSearchTerm(
                                                  e.target.value,
                                                )
                                              }
                                              className="block w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-3 pr-12 text-sm focus:border-sky-500 focus:outline-none focus:ring-0"
                                              placeholder="Search customers"
                                            />
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400">
                                              {customerSearchTerm ? (
                                                <button
                                                  type="button"
                                                  onClick={
                                                    handleClearCustomerSearchFilter
                                                  }
                                                  className="mr-2 text-sky-500"
                                                >
                                                  ✕
                                                </button>
                                              ) : null}
                                              <MagnifyingGlassIcon />
                                            </div>
                                          </div>
                                        </div>
                                        <div className="mt-2 space-y-1">
                                          {customers.map((customer) => (
                                            <Listbox.Option
                                              key={customer.id}
                                              className={({ active }) =>
                                                classNames(
                                                  "relative cursor-pointer rounded-xl py-3 pl-3 pr-9 text-sm",
                                                  active
                                                    ? "bg-red-600 text-white"
                                                    : "text-slate-700",
                                                )
                                              }
                                              value={customer}
                                            >
                                              {({ selected, active }) => (
                                                <>
                                                  <span
                                                    className={classNames(
                                                      selected
                                                        ? "font-semibold"
                                                        : "font-medium",
                                                      "block truncate",
                                                    )}
                                                  >
                                                    {customer.name}
                                                  </span>
                                                  {selected ? (
                                                    <span
                                                      className={classNames(
                                                        active
                                                          ? "text-white"
                                                          : "text-red-600",
                                                        "absolute inset-y-0 right-0 flex items-center pr-4",
                                                      )}
                                                    >
                                                      <CheckIcon className="h-5 w-5" />
                                                    </span>
                                                  ) : null}
                                                </>
                                              )}
                                            </Listbox.Option>
                                          ))}
                                        </div>
                                      </Listbox.Options>
                                    </Transition>
                                  </div>
                                )}
                              </Listbox>
                            </FilterShell>
                          ) : (
                            <FilterShell>
                              <div className="rounded-xl bg-white px-4 py-3.5 text-sm text-slate-400">
                                Customer filter unavailable
                              </div>
                            </FilterShell>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>

          {loading ? (
            <div className="mt-6">
              <Loader />
            </div>
          ) : null}

          {showCombinedView ? (
            <div className="mt-6 space-y-6">
              {showSpendingInfo ? (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                  <StatCard
                    label="Total Invoiced"
                    value={`$${totalSpent.toLocaleString()}`}
                    helper="Standard & retainers"
                  />
                  <StatCard
                    label="Total Not Invoiced"
                    value={`$${totalSpentNotInvoiced.toLocaleString()}`}
                    helper="Standard & retainers"
                    accent="sky"
                  />
                </div>
              ) : null}

              <SectionCard className="overflow-hidden">
                <div className="border-b border-slate-200 px-4 py-4 sm:px-6">
                  <div className="inline-flex rounded-2xl bg-slate-100 p-1">
                    <button
                      type="button"
                      onClick={handleSelectStandServices}
                      className={classNames(
                        "rounded-xl px-4 py-2 text-sm font-semibold transition",
                        isStandardServicesSelected
                          ? "bg-white text-red-600 shadow-sm"
                          : "text-slate-600 hover:text-slate-900",
                      )}
                    >
                      Standard Services
                    </button>
                    <button
                      type="button"
                      onClick={handleSelectRetainerServices}
                      className={classNames(
                        "rounded-xl px-4 py-2 text-sm font-semibold transition",
                        isRetainerServicesSelected
                          ? "bg-white text-red-600 shadow-sm"
                          : "text-slate-600 hover:text-slate-900",
                      )}
                    >
                      Retainer Services
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 xl:grid-cols-4 sm:p-6">
                  <StatCard
                    label="Number of Services"
                    value={numberOfServices.toLocaleString()}
                    helper={dateSelected.name}
                  />
                  <StatCard
                    label="Number of Tails"
                    value={numberOfTails.toLocaleString()}
                    helper="Unique aircraft"
                    accent="sky"
                  />
                  <StatCard
                    label="Number of Airports"
                    value={numberOfLocations.toLocaleString()}
                    helper="Unique locations"
                  />
                  <StatCard
                    label="Labor Hours"
                    value={Number(totalLaborTime || 0).toLocaleString()}
                    helper="Total labor time"
                  />
                </div>
              </SectionCard>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 m:-grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
              {showSpendingInfo && !selectedRetainerService ? (
                <StatCard
                  label="Total"
                  value={`$${totalSpent.toLocaleString()}`}
                  helper={
                    selectedService?.name === "All Services" && showRetainers
                      ? "Standard & retainers"
                      : dateSelected.name
                  }
                />
              ) : null}
              <StatCard
                label="Number of Services"
                value={numberOfServices.toLocaleString()}
                helper={dateSelected.name}
                accent="sky"
              />
              <StatCard
                label="Number of Tails"
                value={numberOfTails.toLocaleString()}
                helper="Unique aircraft"
              />
              <StatCard
                label="Number of Airports"
                value={numberOfLocations.toLocaleString()}
                helper="Unique locations"
                accent="sky"
              />

              <StatCard
                label="Labor Hours"
                value={Number(totalLaborTime || 0).toLocaleString()}
                helper="Total labor time"
              />
            </div>
          )}

          <div className="mt-6 space-y-6">
            {isStandardServicesSelected ? (
              totalServiceActivities === 0 && !activitiesLoading ? (
                <EmptyState
                  title="No standard services found"
                  description="Try changing your search criteria."
                />
              ) : (
                <>
                  {activitiesLoading ? <Loader /> : null}
                  {totalServiceActivities > 0
                    ? renderActivityTable(
                        serviceActivities,
                        totalServiceActivities,
                        "Standard Services Completed",
                        "All standard services completed during the selected time period.",
                        currentPage,
                        handlePageChange,
                      )
                    : null}
                </>
              )
            ) : null}

            {isRetainerServicesSelected ? (
              totalRetainerServiceActivities === 0 && !activitiesLoading ? (
                <EmptyState
                  title="No retainer services found"
                  description="Try changing your search criteria."
                />
              ) : (
                <>
                  {activitiesLoading ? <Loader /> : null}
                  {totalRetainerServiceActivities > 0
                    ? renderActivityTable(
                        retainerServiceActivities,
                        totalRetainerServiceActivities,
                        "Retainer Services Completed",
                        "All retainer services completed during the selected time period.",
                        currentRetainerPage,
                        handleRetainerPageChange,
                      )
                    : null}
                </>
              )
            ) : null}
          </div>
        </main>
      </div>

      {isExportJobModalOpen && (
        <ExportJobModal
          isOpen={isExportJobModalOpen}
          handleClose={handleToggleExportJobModal}
          handleExport={handleExport}
        />
      )}
    </div>
  );
}
